import { Component, OnInit, OnDestroy, Injectable } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs/Subject';
import { APIModelInterface, BaseAPIModel } from '../shared/api.model';
import { Filter } from './filter';
import { BaseLoopBackApiService } from '../services/api/loopback/base.service';
import { BaseLoopBackStorageService } from '../services/storage/loopback/base.service';
import { SWAL_COMMMON, SwalErrorHandler, PAGE_ACTION, ITEMS_PER_PAGE, OPERATOR, MULTISELECT_LIMIT } from './constants';
import { BaseHelperService } from '../services/helper/base.service';
import { FilterHandler } from './filter-handler';

declare const swal: any;

@Injectable()
export abstract class APIComponent implements OnInit, OnDestroy {
  protected ngUnsubscribe: Subject<void> = new Subject<void>();
  public filters: Filter;
  public headers: Array<string> = [];
  public items: Array<any> = [];
  public item: any = null;
  public form: FormGroup = null;
  public path: string = '';
  public page: string = '';
  public operator = OPERATOR;

  public constructor(
    protected route: ActivatedRoute,
    protected router: Router,
    protected formBuilder: FormBuilder,
    protected storageService: BaseLoopBackStorageService,
    protected apiService: BaseLoopBackApiService
  ) {
    this.initFilter();
  }

  public ngOnInit() {
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next(); // Emit a value
    this.ngUnsubscribe.complete(); // complete the observable
  }

  public initFilter() {
    this.filters = new Filter({
      skip: 0,
      limit: ITEMS_PER_PAGE,
      page: 1,
      totalPages: 1
    });
  }

  public create() {
    this.router.navigate([this.path + '/create']);
  }

  public edit(id) {
    this.router.navigate([this.path + '/edit'], { queryParams: { id: id } });
  }

  public back() {
    this.router.navigate([this.path]);
  }

  public updateFilter(event: any, fields: any, options: any = {}) {
    let self = this;
    let value = event;
    if (event.target && event.target.value !== undefined) {
      value = event.target.value;
    }
    if (!Array.isArray(fields)) {
      fields = [fields];
    }
    var oldUnionType = options.unionType || 'and';
    for(var index in fields) {
      var field = fields[index];
      options.removeUnicode = true;
      options.unionType = oldUnionType;
      self.filters.addCriteria(field, value, options);
      if (options.operator && options.operator === OPERATOR.LIKE && isNaN(value)) {
        options.unionType = 'or';
        self.filters.addCriteria(field, value, options);
      }
    };
  }

  public getTotalPages() {
    let filter = this.filters.toString();
    let where = filter.where ? filter.where : {};
    this.apiService.count(where)
      .takeUntil(this.ngUnsubscribe)
      .subscribe(res => {
        this.filters.updateTotalPages(res.data.count);
      }, error => {
        swal(SwalErrorHandler.errorMessage(error));
      });
  }

  public changePage(event) {
    this.filters.updatePageOffset(event);
    this.loadItems();
  }

  public resetPage(event) {

  }

  public loadItems() {
    this.apiService.find(new Filter(this.filters.toString()))
      .takeUntil(this.ngUnsubscribe)
      .subscribe(res => {
        this.items = res.data;
        this.getTotalPages();
      }, error => {
        swal(SwalErrorHandler.errorMessage(error));
      });
  }

  public loadItem() {
    const _self = this;
    _self.route.queryParams
      .takeUntil(_self.ngUnsubscribe)
      .subscribe(params => {
        if (params['id']) {
          const filter = new Filter(this.filters.toString());
          _self.apiService.findById(params['id'], filter)
            .takeUntil(this.ngUnsubscribe)
            .subscribe(res => {
              this.bindData(res.data);
              this.form.patchValue(this.item);
            }, error => {
              swal(SwalErrorHandler.errorMessage(error));
            });
        } else {
          this.item = new BaseAPIModel();
        }
      }, error => {
        swal(SwalErrorHandler.errorMessage(error));
      });
  }

  public bindData(data) {
    this.item = data;
    if (data['code']) {
      this.item['id'] = data['code'];
    }
  }

  public onSave() {
    let data = this.form.value;
    if (this.page == PAGE_ACTION.CREATE) {
      this.apiService.create(data)
        .takeUntil(this.ngUnsubscribe)
        .subscribe(res => {
          swal(SWAL_COMMMON.UPDATE_SUCCESS).then(() => {
            this.router.navigate([this.path]);
          });
        }, error => {
          swal(SwalErrorHandler.errorMessage(error));
        });
    } else if (this.page === PAGE_ACTION.EDIT) {
      this.apiService.patchAttributes(this.item.id, data)
        .takeUntil(this.ngUnsubscribe)
        .subscribe(res => {
          swal(SWAL_COMMMON.UPDATE_SUCCESS).then(() => {
            this.router.navigate([this.path]);
          });
        }, error => {
          swal(SwalErrorHandler.errorMessage(error));
        });
    }
  }

  public reset(id) {
    swal(SWAL_COMMMON.RESET_WARNING).then((result) => {
      if (result.value) {
        this.apiService.resetById(id)
          .takeUntil(this.ngUnsubscribe)
          .subscribe(res => {
            swal(SWAL_COMMMON.RESET_SUCCESS);
            this.loadItems();
          }, error => {
            swal(SwalErrorHandler.errorMessage(error));
          });
      }
    }, () => {
    });
  }

  public delete(id) {
    swal(SWAL_COMMMON.DELETE_WARNING).then((result) => {
      if (result.value) {
        this.apiService.deleteById(id)
          .takeUntil(this.ngUnsubscribe)
          .subscribe(res => {
            swal(SWAL_COMMMON.DELETE_SUCCESS);
            this.loadItems();
          }, error => {
            swal(SwalErrorHandler.errorMessage(error));
          });
      }
    }, () => {
    });
  }

  public uploadedImage(event) {
    this.item['image'] = event;
    if (this.form.controls['image']) {
      this.form.controls['image'].setValue(event);
    }
  }

  /**
   * Init settings for multiple select list
   *
   * @param settings (text, classes, enableSearchFilter, limitSelection, singleSelection)
   */
  public initMultiselectSettings(settings: any): object {
    settings = settings || {};
    settings.text = settings.text || 'Select items';
    settings.classes = settings.classes || 'myclass mga-multiple-choice';
    settings.enableSearchFilter = settings.enableSearchFilter !== undefined ? settings.enableSearchFilter : true;
    settings.limitSelection = settings.limitSelection !== undefined ? settings.limitSelection : MULTISELECT_LIMIT;
    settings.singleSelection = settings.singleSelection !== undefined ? settings.singleSelection : false;
    return settings;
  }

  /**
   * Check a form is valid if all fields are valid
   *
   * @param formControls
   */
  public isFormValidated(formControls) {
    let isValid = true;
    let fieldNames = Object.keys(formControls);
    fieldNames.forEach(function(fieldName) {
      let field = formControls[fieldName];
      if(field.controls) {
        for(let fieldName2 in field.controls) {
          let field2 = field.controls[fieldName2];
          isValid = isValid && field2.valid;
        }
      }
      isValid = isValid && field.valid;
    });
    return isValid;
  }

  /**
   * Convert array objects to array ids
   * @param objects
   */
  public getSelectedValues(objects) {
    const selectList = [];
    for (const object of objects) {
      if (object.id) {
        selectList.push(object.id);
      } else if (object.code) {
        selectList.push(object.code);
      } else if (object.username) {
        selectList.push(object.username);
      }
    }
    return selectList;
  }

  /**
   * Convert from api list to select list
   * @param objects
   */
  public convertToSelectList(objects: any, mapping: any = {}) {
    const selectList = [];
    for (const object of objects) {
      let item = {};
      for (const key in mapping) {
        let object_key = key;
        let mapping_key = mapping[key];
        if (object[mapping_key] != undefined) {
          item[object_key] = object[mapping_key];
        }
      }
      selectList.push(item);
    }
    return selectList;
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/api.component.ts